import { OverlayPortal as FloatingPortal } from "@/components/modal/OverlayPortal";
import { ButtonV2, IconButtonV2, LinkButtonV2 } from "@/components/interactive/ButtonV2";
import { ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";

import { useId } from "react";

export type NudgeCardV2Action = {
  label: string;
} & ({ onClick: () => void; href?: never } | { href: string; onClick?: never });

export interface NudgeCardV2Props {
  title: string;
  description: string;
  actions: NudgeCardV2Action[];
  /** Omit to hide the close button. The caller owns visibility and dismissal persistence. */
  onDismiss?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Which bottom corner the card sits in. Defaults to the left. */
  placement?: "bottom-left" | "bottom-right";
  /** Extra clearance above the viewport's bottom edge, for chrome that runs along it such as the
   *  assistant rail. Added to the card's own 16px inset. */
  bottomOffset?: number;
}

/** Non-modal product announcement, portaled so page overflow/transforms cannot clip it. */
export function NudgeCardV2({
  title,
  description,
  actions,
  onDismiss,
  onMouseEnter,
  onMouseLeave,
  placement = "bottom-left",
  bottomOffset = 0,
}: NudgeCardV2Props) {
  const id = useId();

  return (
    <FloatingPortal>
      <section
        data-ds-v2=""
        aria-labelledby={`${id}-title`}
        aria-describedby={`${id}-description`}
        className={classNames(
          "design-system-typography fixed w-80 max-w-[calc(100vw-32px-env(safe-area-inset-left)-env(safe-area-inset-right))] max-h-[calc(100dvh-32px-env(safe-area-inset-top)-env(safe-area-inset-bottom))] overflow-y-auto",
          placement === "bottom-left"
            ? "left-[max(16px,env(safe-area-inset-left))]"
            : "right-[max(16px,env(safe-area-inset-right))]",
          "no-scrollbar-gutter-subtree rounded-cds-6 border-[1px] border-cds-surface-border bg-cds-surface-default p-cds-16 shadow-cds-m dark:border-white/[0.08] dark:bg-cds-elevated-dark dark:shadow-cds-l-dark",
          ZIndexClass.PRODUCT_NUDGE,
        )}
        style={{ bottom: `calc(max(16px, env(safe-area-inset-bottom)) + ${bottomOffset}px)` }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className={onDismiss != null ? "pr-cds-24" : undefined}>
          <h2
            id={`${id}-title`}
            className="min-w-0 flex-1 break-words text-cds-md font-medium text-cds-ink-strong dark:text-zinc-100"
          >
            {title}
          </h2>
          {onDismiss != null && (
            <IconButtonV2
              aria-label={`Dismiss ${title}`}
              icon="x"
              size="xs"
              variant="tertiary"
              onClick={onDismiss}
              className="absolute right-cds-6 top-cds-6"
            />
          )}
        </div>
        <p
          id={`${id}-description`}
          className="mt-cds-6 break-words text-cds-sm text-cds-ink-secondary dark:text-zinc-400"
        >
          {description}
        </p>
        <div className="mt-cds-16 flex flex-wrap gap-cds-8">
          {actions.map((renderedAction) =>
            renderedAction.href != null ? (
              <LinkButtonV2
                key={renderedAction.label}
                href={renderedAction.href}
                variant="secondary"
                size="sm"
              >
                {renderedAction.label}
              </LinkButtonV2>
            ) : (
              <ButtonV2
                key={renderedAction.label}
                type="button"
                onClick={renderedAction.onClick}
                variant="secondary"
                size="sm"
              >
                {renderedAction.label}
              </ButtonV2>
            ),
          )}
        </div>
      </section>
    </FloatingPortal>
  );
}
